'use client';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { DEFAULT_MAX_CREDENTIAL_CIDR_COUNT } from 'shared/src/client/constants';

export type MaestroFlags = {
  // Boolean flags
  cloudAllowOrgCreation: boolean;
  cloudAuthLogForwardingEnabled: boolean;
  cloudAuthLogForwardingUseCriblHttpSource: boolean;
  cloudAuthLogForwardingUiVerifyBeforeSaving: boolean;
  cloudAwsMarketplaceEnabled: boolean;
  cloudCanDisableHrdSso: boolean;
  cloudClientCredentialsUi: boolean;
  cloudConnectedDeploymentEnabled: boolean;
  cloudConnectedWorkspaceTokenFetchDisabled: boolean;
  cloudControlPlaneCdnUi: boolean;
  cloudEnableAuthLogUi: boolean;
  cloudEnableCourierAudiences: boolean;
  cloudEnterpriseGlobalEnabled: boolean;
  cloudEphemeralSandboxes: boolean;
  cloudIamClientCredentialIpAllowlistEnabled: boolean;
  cloudIamClientCredentialMaxCidrCount: number;
  cloudLayoutCourierPush: boolean;
  cloudMspEnableUiFeatures: boolean;
  cloudOidcGroupsSource: boolean;
  cloudOrgNavUseNewPermissions: boolean;
  cloudInfraServiceReleaseChannels: boolean;
  cloudPortalSsoGroupMappingWarning: boolean;
  cloudPrivateLinkConfigEnabled: boolean;
  cloudReleaseChannelEnabled: boolean;
  cloudReleaseChannels: boolean;
  cloudSsoDisallowSsoUserOrgCreation: boolean;
  cloudSystemEnableFGASystemRolesInMaestroUi: boolean;
  cloudWorkspaceLandingPageDashboard: boolean;
  cloudWorkspaceLandingPageDashboardSystemInsights: boolean;
  cloudWorkspaceHibernationEnabled: boolean;
  cloudConferenceBannerEnabled: boolean;
  cloudZeusAuthzWriteFga: boolean;
  criblCloudEdgeOnboarding: boolean;
  criblCloudNewProvisioningOnboarding: boolean;
  serviceAuth0Available: boolean;
  cloudAuthNewLoginExperience: boolean;
  cloudAuthNewLoginGoogleButton: boolean;
  cloudAuth0ForceUserConnection: boolean;
  cloudInvitationTemplateV2: boolean;
  cloudFullstoryCrossDomainSession: boolean;
  cloudPortalClientTelemetryCollection: boolean;
  cloudMultiRegionWorkspacesEnabled: boolean;
  cloudOrganizationsAllowMultiOrgCreation: boolean;
  cloudSkipPersistedLeaderRedirect: boolean;

  // Number flags
  cloudClientsMaxApiKeys: number;
  cloudOrganizationsMaxOwned: number;
  customAclLimitEnabled: number;

  // String flags
  cloudInsightsUpgrade: 'none' | 'disabled' | 'all-workspaces';
  cloudZeusUiOemTosUrl: string;

  // Array flags
  cloudEphemeralSandboxesOrganizations: string[];
  cloudEphemeralSandboxesCourses: string[];
  cloudProvisioningRegionsBetaAccess: string[];

  // Object flags
  cloudEnableTeamsSelect: { paths: string[] };
  cloudPortalBanner: { link?: string; image?: string; alt?: string };
};

/**
 * Default fallback values for all feature flags.
 * These values are used when LaunchDarkly is offline (returns undefined).
 */
export const DEFAULT_FLAGS: MaestroFlags = {
  // Boolean flags - default to false (conservative/disabled state)
  cloudAllowOrgCreation: false,
  cloudAuthLogForwardingEnabled: false,
  cloudAuthLogForwardingUseCriblHttpSource: false,
  cloudAuthLogForwardingUiVerifyBeforeSaving: false,
  cloudAwsMarketplaceEnabled: false,
  cloudCanDisableHrdSso: false,
  cloudClientCredentialsUi: false,
  cloudConnectedDeploymentEnabled: false,
  cloudConnectedWorkspaceTokenFetchDisabled: false,
  cloudControlPlaneCdnUi: false,
  cloudEnableAuthLogUi: false,
  cloudEnableCourierAudiences: false,
  cloudEnterpriseGlobalEnabled: false,
  cloudEphemeralSandboxes: false,
  cloudIamClientCredentialIpAllowlistEnabled: false,
  cloudIamClientCredentialMaxCidrCount: DEFAULT_MAX_CREDENTIAL_CIDR_COUNT,
  cloudLayoutCourierPush: false,
  cloudMspEnableUiFeatures: false,
  cloudOidcGroupsSource: false,
  cloudOrgNavUseNewPermissions: false,
  cloudInfraServiceReleaseChannels: false,
  cloudPortalSsoGroupMappingWarning: false,
  cloudPrivateLinkConfigEnabled: false,
  cloudReleaseChannelEnabled: false,
  cloudReleaseChannels: false,
  cloudSsoDisallowSsoUserOrgCreation: true, // Default to true to restrict SSO user org creation
  cloudSystemEnableFGASystemRolesInMaestroUi: false,
  cloudWorkspaceLandingPageDashboard: false,
  cloudWorkspaceLandingPageDashboardSystemInsights: false,
  cloudWorkspaceHibernationEnabled: false,
  cloudConferenceBannerEnabled: false,
  cloudZeusAuthzWriteFga: false,
  criblCloudEdgeOnboarding: false,
  criblCloudNewProvisioningOnboarding: false,
  serviceAuth0Available: true, // Default to true to avoid false-positive lockout
  cloudAuthNewLoginExperience: false,
  cloudAuthNewLoginGoogleButton: false,
  cloudAuth0ForceUserConnection: false,
  cloudInvitationTemplateV2: false,
  cloudFullstoryCrossDomainSession: false,
  cloudPortalClientTelemetryCollection: true, // default true: opt-out orgs are served false via LD segment
  cloudMultiRegionWorkspacesEnabled: false,
  cloudOrganizationsAllowMultiOrgCreation: false,
  cloudSkipPersistedLeaderRedirect: false,

  // Number flags
  cloudClientsMaxApiKeys: 10,
  cloudOrganizationsMaxOwned: 1,
  customAclLimitEnabled: 25,

  // String flags
  cloudInsightsUpgrade: 'none',
  cloudZeusUiOemTosUrl: '',

  // Array flags
  cloudEphemeralSandboxesOrganizations: [],
  cloudEphemeralSandboxesCourses: [],
  cloudProvisioningRegionsBetaAccess: [],

  // Object flags
  cloudEnableTeamsSelect: { paths: [] },
  cloudPortalBanner: {},
};

const MAESTRO_FLAG_KEYS = Object.keys(DEFAULT_FLAGS) as (keyof MaestroFlags)[];

const getFlagValue = <T extends keyof MaestroFlags>(flags: Partial<MaestroFlags>, key: T): MaestroFlags[T] =>
  (flags[key] ?? DEFAULT_FLAGS[key]) as MaestroFlags[T];

/**
 * Returns a typed flags object with lazy property access.
 *
 * The LaunchDarkly React SDK wraps `useFlags()` in a Proxy whose `get` trap
 * calls `ldClient.variation()`, emitting an evaluation analytics event for
 * every property read. Eagerly reading all keys (e.g. via Object.fromEntries
 * or spread) therefore fires ~40 evaluation events per component per render,
 * even if the component only checks a single flag.
 *
 * To avoid this, we define per-key getters so that the LD proxy is only
 * touched when a consumer actually accesses a flag. This keeps analytics
 * accurate -- only flags that are read are reported as evaluated.
 */
export const useFlagsProxy = (): MaestroFlags => {
  const flags = useFlags() as Partial<MaestroFlags>;

  return useMemo(() => {
    const lazyFlags = {} as MaestroFlags;

    for (const key of MAESTRO_FLAG_KEYS) {
      Object.defineProperty(lazyFlags, key, {
        enumerable: true,
        get: () => getFlagValue(flags, key),
      });
    }

    return lazyFlags;
  }, [flags]);
};
